import React, { useState, useEffect } from 'react';
import { DashboardLayout } from '../../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Checkbox } from '../../components/ui/checkbox';
import { Switch } from '../../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle
} from '../../components/ui/alert-dialog';
import { Input } from '../../components/ui/input';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  FileText, Download, Calendar, Archive, Clock, CheckCircle2, Loader2,
  AlertCircle, ArrowLeft, CheckCircle, StickyNote, Package, Eye
} from 'lucide-react';
import { Link } from 'react-router-dom';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DateInput = ({ value, onChange, label }) => (
  <div className="space-y-1">
    <Label className="text-sm">{label}</Label>
    <input
      type="date"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
    />
  </div>
);

const getStatusBadge = (status) => {
  if (status === 'completed') {
    return (
      <Badge className="bg-green-100 text-green-800 border-green-300 hover:bg-green-100">
        <CheckCircle className="h-3 w-3 mr-1" /> In WWS übertragen
      </Badge>
    );
  }
  return (
    <Badge variant="outline" className="border-yellow-400 text-yellow-700 bg-yellow-50">
      <Clock className="h-3 w-3 mr-1" /> Noch nicht übertragen
    </Badge>
  );
};

export const MHDExport = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  
  const [locations, setLocations] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [categories, setCategories] = useState({ archived: true, soon: false, ok: false });
  const [archivedFilter, setArchivedFilter] = useState('all'); // "all" | "expired_only" | "sold_only"

  const [rentView, setRentView] = useState('both'); // "both" | "only_writeoff" | "only_delivered"
  const [loading, setLoading] = useState(false);
  const [fetchingData, setFetchingData] = useState(true);
  const [stats, setStats] = useState({ archived: 0, soon: 0, ok: 0, archived_expired: 0, archived_sold: 0 });
  const [includeBarcode, setIncludeBarcode] = useState(true); // Barcode-Toggle

  // Export history
  const [exports, setExports] = useState([]);
  const [loadingExports, setLoadingExports] = useState(true);

  // Complete dialog
  const [showCompleteDialog, setShowCompleteDialog] = useState(false);
  const [completingExport, setCompletingExport] = useState(null);
  const [completeNote, setCompleteNote] = useState('');
  const [completing, setCompleting] = useState(false);

  useEffect(() => {
    fetchInitialData();
    fetchExports();
    const now = new Date();
    const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
    const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    setDateFrom(firstDay.toISOString().split('T')[0]);
    setDateTo(lastDay.toISOString().split('T')[0]);
  }, []);

  useEffect(() => { fetchStats(); }, [selectedLocation]);

  const fetchInitialData = async () => {
    try {
      const locationsRes = await axios.get(`${API}/user/locations`);
      setLocations(locationsRes.data);
    } catch (error) {
      console.error('Failed to fetch locations:', error);
    } finally {
      setFetchingData(false);
    }
  };

  const fetchExports = async () => {
    setLoadingExports(true);
    try {
      const res = await axios.get(`${API}/mhd/exports`);
      setExports(res.data || []);
    } catch {
      setExports([]);
    } finally {
      setLoadingExports(false);
    }
  };

  const fetchStats = async () => {
    try {
      const [productsRes, writeoffsRes] = await Promise.all([
        axios.get(`${API}/mhd/products${selectedLocation !== 'all' ? `?location_id=${selectedLocation}` : ''}`),
        axios.get(`${API}/mhd/writeoffs${selectedLocation !== 'all' ? `?location_id=${selectedLocation}` : ''}`)
      ]);
      const products = productsRes.data?.items || productsRes.data || [];
      const writeoffs = writeoffsRes.data || [];
      const settingsRes = await axios.get(`${API}/mhd/settings`);
      const warningDays = settingsRes.data?.warning_days || 7;
      const today = new Date(); today.setHours(0, 0, 0, 0);
      const todayStr = today.toISOString().split('T')[0];
      const warningDate = new Date(today); warningDate.setDate(warningDate.getDate() + warningDays);
      const warningDateStr = warningDate.toISOString().split('T')[0];
      setStats({
        archived: writeoffs.length,
        archived_expired: writeoffs.filter(w => w.removal_type !== 'sold').length,
        archived_sold: writeoffs.filter(w => w.removal_type === 'sold').length,
        soon: products.filter(p => p.expiry_date > todayStr && p.expiry_date <= warningDateStr).length,
        ok: products.filter(p => p.expiry_date > warningDateStr).length
      });
    } catch (error) { console.error('Failed to fetch stats:', error); }
  };

  const toggleCategory = (cat) => setCategories(prev => ({ ...prev, [cat]: !prev[cat] }));

  const handleExport = async () => {
    const selectedCategories = Object.keys(categories).filter(k => categories[k]);
    if (selectedCategories.length === 0) { toast.error('Bitte mindestens eine Kategorie auswählen'); return; }
    setLoading(true);
    try {
      // Schritt 1: Export erstellen (JSON-Antwort wie Backshop)
      const res = await axios.post(`${API}/mhd/export-pdf`, {
        location_id: selectedLocation !== 'all' ? selectedLocation : null,
        date_from: dateFrom || null,
        date_to: dateTo || null,
        categories: selectedCategories,
        include_barcode: includeBarcode,
        archived_filter: archivedFilter,
        rent_view: rentView,
      });
      
      const exportId = res.data.export_id;

      // Schritt 2: Liste sofort refreshen (Export erscheint im Verlauf)
      await fetchExports();

      // Schritt 3: PDF automatisch herunterladen
      try {
        const pdfRes = await axios.get(`${API}/mhd/exports/${exportId}/pdf`, { responseType: 'blob' });
        const blob = new Blob([pdfRes.data], { type: 'application/pdf' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = res.data.filename || `MHD-Export-${exportId}.pdf`;
        document.body.appendChild(link); link.click(); document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      } catch { /* PDF-Download-Fehler ist nicht kritisch */ }

      toast.success(`Export erstellt: ${res.data.total_products} Produkte`);

      // Export-Verlauf einblenden
      setTimeout(() => {
        document.querySelector('[data-export-history]')?.scrollIntoView({ behavior: 'smooth' });
      }, 300);
    } catch (error) {
      toast.error('Export fehlgeschlagen');
    } finally {
      setLoading(false);
    }
  };

  const downloadExportPDF = async (exportId, filename) => {
    try {
      const response = await axios.get(`${API}/mhd/exports/${exportId}/pdf`, { responseType: 'blob' });
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url; link.download = filename || `MHD-Export-${exportId}.pdf`;
      document.body.appendChild(link); link.click(); document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch { toast.error('Download fehlgeschlagen'); }
  };

  const openCompleteDialog = (exp) => {
    setCompletingExport(exp); setCompleteNote(''); setShowCompleteDialog(true);
  };

  const handleComplete = async () => {
    if (!completingExport) return;
    setCompleting(true);
    try {
      await axios.put(`${API}/mhd/exports/${completingExport.id}/complete`, { note: completeNote });
      toast.success('Als "In WWS übertragen" markiert');
      setShowCompleteDialog(false);
      await fetchExports();
    } catch { toast.error('Fehler beim Markieren'); }
    finally { setCompleting(false); }
  };

  const setQuickDateRange = (range) => {
    const today = new Date();
    let from, to;
    switch (range) {
      case 'today': from = to = today.toISOString().split('T')[0]; break;
      case 'week':
        from = new Date(today.setDate(today.getDate() - 7)).toISOString().split('T')[0];
        to = new Date().toISOString().split('T')[0]; break;
      case 'month':
        from = new Date(today.getFullYear(), today.getMonth(), 1).toISOString().split('T')[0];
        to = new Date().toISOString().split('T')[0]; break;
      case 'lastMonth':
        from = new Date(today.getFullYear(), today.getMonth() - 1, 1).toISOString().split('T')[0];
        to = new Date(today.getFullYear(), today.getMonth(), 0).toISOString().split('T')[0]; break;
      case 'quarter':
        const qs = Math.floor(today.getMonth() / 3) * 3;
        from = new Date(today.getFullYear(), qs, 1).toISOString().split('T')[0];
        to = new Date().toISOString().split('T')[0]; break;
      default: from = ''; to = '';
    }
    setDateFrom(from); setDateTo(to);
  };

  const formatDate = (str) => { try { return new Date(str).toLocaleString('de-DE'); } catch { return str; } };

  if (fetchingData) {
    return <DashboardLayout><div className="flex items-center justify-center h-96"><Loader2 className="h-12 w-12 animate-spin text-primary" /></div></DashboardLayout>;
  }

  const selectedCount = Object.values(categories).filter(Boolean).length;

  return (
    <DashboardLayout>
      <div className="space-y-6 max-w-2xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-3">
          <Link to="/admin/mhd-control">
            <Button variant="ghost" size="icon"><ArrowLeft className="h-5 w-5" /></Button>
          </Link>
          <div>
            <h1 className="text-2xl font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>MHD Export</h1>
            <p className="text-muted-foreground text-sm">PDF-Bericht erstellen & WWS-Status verfolgen</p>
          </div>
        </div>

        {/* Export Form */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2"><FileText className="h-5 w-5 text-blue-600" />Bericht konfigurieren</CardTitle>
            <CardDescription>Zeitraum und Kategorien für den Export wählen</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {locations.length > 1 && (
              <div className="space-y-2">
                <Label>Standort</Label>
                <Select value={selectedLocation} onValueChange={setSelectedLocation}>
                  <SelectTrigger><SelectValue placeholder="Alle Standorte" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Alle Standorte</SelectItem>
                    {locations.map(loc => <SelectItem key={loc.id} value={loc.id}>{loc.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}

            <div className="space-y-3">
              <Label className="flex items-center gap-2"><Calendar className="h-4 w-4" />Zeitraum</Label>
              <div className="flex flex-wrap gap-2">
                {[['today','Heute'],['week','7 Tage'],['month','Monat'],['lastMonth','Vormonat'],['quarter','Quartal']].map(([k,l]) => (
                  <Button key={k} variant="outline" size="sm" onClick={() => setQuickDateRange(k)}>{l}</Button>
                ))}
                <Button variant="outline" size="sm" onClick={() => { setDateFrom(''); setDateTo(''); }}>Alle</Button>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <DateInput label="Von" value={dateFrom} onChange={setDateFrom} />
                <DateInput label="Bis" value={dateTo} onChange={setDateTo} />
              </div>
            </div>

            <div className="space-y-3">
              <Label>Kategorien</Label>
              {[
                { key: 'soon', icon: Clock, color: 'yellow', label: 'Bald ablaufend', count: stats.soon },
                { key: 'ok', icon: CheckCircle2, color: 'green', label: 'Noch haltbar', count: stats.ok },
                { key: 'archived', icon: Archive, color: 'gray', label: 'Archiviert (Abgeschrieben)', count: stats.archived },
              ].map(({ key, icon: Icon, color, label, count }) => (
                <div key={key}>
                  <div
                    onClick={() => toggleCategory(key)}
                    className={`flex items-center justify-between p-4 rounded-lg border-2 cursor-pointer transition-all ${categories[key] ? `border-${color}-500 bg-${color}-50` : 'border-gray-200 hover:border-gray-300'}`}
                  >
                    <div className="flex items-center gap-3">
                      <Icon className={`h-5 w-5 text-${color}-500`} />
                      <p className="font-medium">{label}</p>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className={`text-sm font-semibold text-${color}-600`}>{count}</span>
                      <Checkbox checked={categories[key]} />
                    </div>
                  </div>

                  {/* Sub-Filter für Archiviert */}
                  {key === 'archived' && categories.archived && (
                    <div className="ml-4 mt-2 space-y-1.5 border-l-2 border-gray-200 pl-4">
                      {[
                        { value: 'all', label: 'Alles', count: stats.archived, desc: 'Abgelaufene + Verkaufte' },
                        { value: 'expired_only', label: 'Nur abgelaufene', count: stats.archived_expired, desc: 'Für WWS-Abschreibung' },
                        { value: 'sold_only', label: 'Nur verkaufte', count: stats.archived_sold, desc: 'Abverkaufte Artikel' },
                      ].map(opt => (
                        <div
                          key={opt.value}
                          onClick={() => setArchivedFilter(opt.value)}
                          className={`flex items-center justify-between px-3 py-2 rounded-lg cursor-pointer border transition-all ${
                            archivedFilter === opt.value
                              ? 'border-gray-500 bg-gray-100 dark:bg-gray-800'
                              : 'border-transparent hover:bg-gray-50 dark:hover:bg-gray-900'
                          }`}
                        >
                          <div className="flex items-center gap-2">
                            <div className={`w-3.5 h-3.5 rounded-full border-2 flex items-center justify-center shrink-0 ${
                              archivedFilter === opt.value ? 'border-gray-600' : 'border-gray-400'
                            }`}>
                              {archivedFilter === opt.value && <div className="w-2 h-2 rounded-full bg-gray-600" />}
                            </div>
                            <div>
                              <p className="text-sm font-medium text-foreground">{opt.label}</p>
                              <p className="text-xs text-muted-foreground">{opt.desc}</p>
                            </div>
                          </div>
                          <span className="text-xs font-semibold text-gray-500 bg-gray-100 dark:bg-gray-700 px-2 py-0.5 rounded-full">
                            {opt.count}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>

            <div className="pt-4 border-t">
              {/* Barcode Toggle – wie beim Backshop */}
              <div
                className={`flex items-center justify-between p-3 rounded-lg border cursor-pointer mb-3 transition-all ${
                  includeBarcode
                    ? 'border-green-500 bg-green-50 dark:bg-green-900/20 dark:border-green-700'
                    : 'border-border bg-muted/30 dark:bg-muted/10'
                }`}
                onClick={() => setIncludeBarcode(v => !v)}
              >
                <div className="flex items-center gap-3">
                  <div className={`text-xl ${includeBarcode ? '' : 'grayscale opacity-50'}`}>
                    {includeBarcode ? '▮▯▮▮▯▮' : '▮▮▯▮▯▮'}
                  </div>
                  <div>
                    <p className="font-medium text-sm">{includeBarcode ? '✓ Mit Barcode' : 'Ohne Barcode'}</p>
                    <p className="text-xs text-muted-foreground">
                      {includeBarcode ? 'EAN/Art.Nr. als CODE128-Barcode im PDF' : 'Ohne Barcode-Spalte'}
                    </p>
                  </div>
                </div>
                <Switch checked={includeBarcode} onCheckedChange={setIncludeBarcode} onClick={(e) => e.stopPropagation()} />
              </div>

              {/* Rentabilitäts-Seite: Filter */}
              <div className="mb-3">
                <p className="text-xs font-semibold text-muted-foreground uppercase mb-2">Rentabilitäts-Übersicht (letzte Seite)</p>
                <div className="grid grid-cols-3 gap-1.5">
                  {[
                    { value: 'both', label: 'Alle', desc: 'Geliefert + Abgeschr.' },
                    { value: 'only_writeoff', label: 'Nur Abschr.', desc: 'Nur abgeschriebene' },
                    { value: 'only_delivered', label: 'Nur Lieferg.', desc: 'Nur gelieferte' },
                  ].map(opt => (
                    <button key={opt.value} type="button"
                      onClick={() => setRentView(opt.value)}
                      className={`p-2 rounded-lg border text-xs font-medium transition-all ${
                        rentView === opt.value
                          ? 'bg-blue-600 text-white border-blue-600'
                          : 'bg-background border-border text-muted-foreground hover:border-blue-400'
                      }`}>
                      <div>{opt.label}</div>
                      <div className={`text-xs font-normal mt-0.5 ${rentView === opt.value ? 'text-blue-100' : 'text-muted-foreground'}`}>
                        {opt.desc}
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              <Button onClick={handleExport} disabled={loading || selectedCount === 0} className="w-full h-12 text-base" size="lg" data-testid="mhd-export-btn">
                {loading ? (<><Loader2 className="h-5 w-5 mr-2 animate-spin" />Wird erstellt...</>) : (<><Download className="h-5 w-5 mr-2" />PDF exportieren {selectedCount > 0 && <span className="ml-2 text-sm opacity-80">({selectedCount} {selectedCount === 1 ? 'Kategorie' : 'Kategorien'})</span>}</>)}
              </Button>
              {selectedCount === 0 && (
                <p className="text-sm text-red-500 text-center mt-2 flex items-center justify-center gap-1">
                  <AlertCircle className="h-4 w-4" />Bitte mindestens eine Kategorie auswählen
                </p>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Export-Verlauf */}
        <Card data-export-history>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Package className="h-5 w-5 text-blue-600" />Export-Verlauf
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loadingExports ? (
              <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
            ) : exports.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <FileText className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                <p>Noch keine Exporte erstellt</p>
              </div>
            ) : (
              <div className="space-y-4">
                {exports.map(exp => (
                  <div key={exp.id} className={`border-2 rounded-lg p-4 transition-all hover:shadow-md ${exp.status === 'completed' ? 'border-green-200 bg-green-50/40 dark:bg-green-950/10 dark:border-green-800' : 'border-yellow-200 bg-yellow-50/40 dark:bg-yellow-950/10 dark:border-yellow-800'}`} data-testid={`export-record-${exp.id}`}>
                    <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2 flex-wrap">
                          <span className="font-mono text-xs text-gray-500">#{exp.id}</span>
                          {getStatusBadge(exp.status)}
                        </div>
                        <div className="space-y-1 text-sm">
                          <div className="flex items-center gap-2 text-gray-700 dark:text-gray-300">
                            <Calendar className="h-4 w-4 text-gray-400" />
                            <span className="font-medium">
                              {exp.date_from ? `${new Date(exp.date_from).toLocaleDateString('de-DE')} – ${new Date(exp.date_to).toLocaleDateString('de-DE')}` : 'Alle Zeiträume'}
                            </span>
                          </div>
                          <div className="flex flex-wrap gap-2 text-xs mt-1">
                            {exp.categories?.map(c => (
                              <Badge key={c} variant="outline">{c === 'archived' ? 'Abgeschrieben' : c === 'soon' ? 'Bald ablaufend' : 'Noch haltbar'}</Badge>
                            ))}
                            <Badge variant="outline">{exp.total_products} Produkte</Badge>
                            {exp.location_name && <Badge variant="outline">{exp.location_name}</Badge>}
                          </div>
                          <p className="text-xs text-gray-500 mt-1">
                            Exportiert: {formatDate(exp.exported_at)} von {exp.exported_by_name}
                          </p>
                          {exp.note && (
                            <div className="flex items-start gap-2 mt-2 p-2 bg-blue-50 dark:bg-blue-950/20 rounded text-xs">
                              <StickyNote className="h-4 w-4 text-blue-600 flex-shrink-0 mt-0.5" />
                              <span>{exp.note}</span>
                            </div>
                          )}
                          {exp.completed_at && (
                            <p className="text-xs text-green-600 dark:text-green-400 mt-1">
                              ✓ Übertragen am: {formatDate(exp.completed_at)} von {exp.completed_by_name}
                            </p>
                          )}
                        </div>
                      </div>
                      <div className="flex flex-col gap-2 min-w-[140px]">
                        <Button variant="default" size="sm" onClick={() => navigate(`/admin/mhd-export/${exp.id}`)} className="w-full sm:w-auto bg-blue-600 hover:bg-blue-700" data-testid={`view-export-${exp.id}`}>
                          <Eye className="h-4 w-4 mr-1" />Ansehen
                        </Button>
                        <Button variant="outline" size="sm" onClick={() => downloadExportPDF(exp.id, exp.filename)} data-testid={`download-pdf-${exp.id}`}>
                          <Download className="h-4 w-4 mr-1" />PDF laden
                        </Button>
                        {exp.status === 'pending' && (
                          <Button size="sm" onClick={() => openCompleteDialog(exp)} className="bg-green-600 hover:bg-green-700 text-white" data-testid={`mark-wws-${exp.id}`}>
                            <CheckCircle className="h-4 w-4 mr-1" />In WWS eintragen
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Complete Dialog */}
      <AlertDialog open={showCompleteDialog} onOpenChange={setShowCompleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>In WWS übertragen bestätigen</AlertDialogTitle>
            <AlertDialogDescription>
              Dieser Export wird als "In WWS übertragen" markiert. Optional können Sie eine Notiz hinzufügen.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="my-4">
            <Label>Notiz (optional)</Label>
            <Input
              value={completeNote}
              onChange={(e) => setCompleteNote(e.target.value)}
              placeholder="z.B. Übertragen von Max Mustermann"
              className="mt-2"
              data-testid="complete-note-input"
            />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={completing}>Abbrechen</AlertDialogCancel>
            <AlertDialogAction onClick={handleComplete} disabled={completing} className="bg-green-600 hover:bg-green-700" data-testid="confirm-complete-btn">
              {completing ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <CheckCircle className="h-4 w-4 mr-1" />}
              Bestätigen
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </DashboardLayout>
  );
};

